import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { ArrowRight, TrendUp, FileText, Briefcase, Certificate, ChartLine } from "@phosphor-icons/react";

const HomePage = () => {
  const services = [
    {
      id: "business-setup",
      title: "Business Setup",
      description: "Complete guidance for business registration, licensing, and compliance.",
      icon: <Briefcase size={32} />,
      gridClass: "md:col-span-6"
    },
    {
      id: "audit",
      title: "Audit Services",
      description: "Comprehensive audit and assurance services for financial transparency.",
      icon: <FileText size={32} />,
      gridClass: "md:col-span-6"
    },
    {
      id: "taxation",
      title: "Taxation",
      description: "Expert tax planning, filing, and compliance for individuals and businesses.",
      icon: <ChartLine size={32} />,
      gridClass: "md:col-span-4"
    },
    {
      id: "company-law",
      title: "Company Law",
      description: "Legal compliance and advisory for corporate governance.",
      icon: <Certificate size={32} />,
      gridClass: "md:col-span-4"
    },
    {
      id: "fund-raising",
      title: "Fund Raising",
      description: "Strategic support for capital raising and financial planning.",
      icon: <TrendUp size={32} />,
      gridClass: "md:col-span-4"
    }
  ];

  const industries = [
    "Small & Medium Enterprises (SMEs)",
    "Startups & Entrepreneurs",
    "Manufacturing & Production",
    "Retail & E-commerce",
    "Healthcare & Pharmaceuticals",
    "Technology & IT Services",
    "Real Estate & Construction",
    "Financial Institutions",
    "Non-Profit Organizations (NGOs)",
    "Professional Services"
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 sm:pt-40 sm:pb-32 px-6 sm:px-12 lg:px-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h1 className="text-5xl sm:text-6xl tracking-tighter font-medium leading-[1.1] mb-6" data-testid="hero-title">
                Expert Financial Solutions for Your Business
              </h1>
              <p className="text-base leading-relaxed text-slate-600 mb-8" data-testid="hero-description">
                N K Borkar & Co. provides comprehensive accounting, audit, and consulting services to help businesses navigate financial complexities with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-900 text-white font-medium hover:bg-slate-800 rounded-sm" data-testid="hero-cta-primary">
                  Get Started <ArrowRight size={20} />
                </Link>
                <a href="/#services" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-slate-900 text-slate-900 font-medium hover:bg-slate-50 rounded-sm" data-testid="hero-cta-secondary">
                  Our Services
                </a>
              </div>
            </div>
            <div className="border border-slate-200 overflow-hidden rounded-sm" data-testid="hero-image-container">
              <img 
                src="https://images.unsplash.com/photo-1641060272821-df59e2c0b5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc0OTc5ODE4fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern office architecture"
                className="w-full h-[500px] object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-slate-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-4" data-testid="about-label">About Us</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6" data-testid="about-title">
                Building Trust Through Professional Excellence
              </h2>
              <p className="text-base leading-relaxed text-slate-600 mb-6" data-testid="about-description-1">
                N K Borkar & Co. is a premier chartered accountancy firm dedicated to delivering comprehensive financial solutions. With years of expertise in accounting, taxation, audit, and business consulting, we empower businesses to achieve sustainable growth.
              </p>
              <p className="text-base leading-relaxed text-slate-600 mb-8" data-testid="about-description-2">
                Our commitment to integrity, accuracy, and client satisfaction has made us a trusted partner for SMEs, startups, corporates, and institutions across diverse industries.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-slate-900 font-medium hover:gap-4 transition-all" data-testid="about-cta">
                Get In Touch <ArrowRight size={20} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-slate-200 overflow-hidden rounded-sm" data-testid="about-image-container">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/12404996-681b-4d8f-80bc-51d2fadf2699/images/e68674284c956ea26e87a6aba91fc1f78167010bcfb1b2f4871d969e50fa7795.png"
                  alt="Professional workspace"
                  className="w-full h-[400px] object-cover"
                  data-testid="about-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section id="services" className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-4" data-testid="services-label">Our Services</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium" data-testid="services-title">Comprehensive Financial Solutions</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`service-tile p-8 bg-white ${service.gridClass}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="text-slate-900 mb-4" data-testid={`service-icon-${service.id}`}>
                  {service.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-medium mb-3" data-testid={`service-title-${service.id}`}>
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed text-slate-600" data-testid={`service-description-${service.id}`}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-slate-50" data-testid="industries-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-4" data-testid="industries-label">Industries We Serve</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6" data-testid="industries-title">Diverse Industry Expertise</h2>
            <p className="text-base leading-relaxed text-slate-600 max-w-3xl" data-testid="industries-description">
              We provide specialized financial services across a wide range of industries, understanding unique challenges and regulatory requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="border border-slate-200 bg-white p-6 flex items-center gap-6"
                data-testid={`industry-item-${index}`}
              >
                <span className="mono text-2xl font-semibold text-slate-300" data-testid={`industry-number-${index}`}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-base font-medium text-slate-900" data-testid={`industry-name-${index}`}>
                  {industry}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24" data-testid="experience-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="border border-slate-200 overflow-hidden rounded-sm" data-testid="experience-image-container">
              <img 
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional team collaboration"
                className="w-full h-[500px] object-cover"
                data-testid="experience-image"
              />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-4" data-testid="experience-label">Experience & Credentials</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6" data-testid="experience-title">
                Proven Track Record of Excellence
              </h2>
              <div className="space-y-6">
                <div className="border-l-2 border-slate-900 pl-6" data-testid="experience-item-1">
                  <h4 className="text-lg sm:text-xl font-medium mb-2">Chartered Accountancy Expertise</h4>
                  <p className="text-base leading-relaxed text-slate-600">
                    Our team comprises qualified chartered accountants with extensive experience in financial management and regulatory compliance.
                  </p>
                </div>
                <div className="border-l-2 border-slate-900 pl-6" data-testid="experience-item-2">
                  <h4 className="text-lg sm:text-xl font-medium mb-2">Client-Centric Approach</h4>
                  <p className="text-base leading-relaxed text-slate-600">
                    We prioritize understanding each client's unique needs to deliver tailored solutions that drive measurable results.
                  </p>
                </div>
                <div className="border-l-2 border-slate-900 pl-6" data-testid="experience-item-3">
                  <h4 className="text-lg sm:text-xl font-medium mb-2">Industry Recognition</h4>
                  <p className="text-base leading-relaxed text-slate-600">
                    Trusted by businesses across multiple sectors for our integrity, professionalism, and commitment to excellence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6" data-testid="cta-title">
            Ready to Transform Your Financial Management?
          </h2>
          <p className="text-base leading-relaxed text-slate-300 mb-8" data-testid="cta-description">
            Let's discuss how N K Borkar & Co. can help your business achieve its financial goals.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 font-medium hover:bg-slate-100 rounded-sm" data-testid="cta-button">
            Contact Us Today <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;